import { z } from 'zod';
import { boardSize } from '../games/spec/interpreter.ts';
import type { Status } from '../games/spec/interpreter.ts';
import {
  MAX_BOARD_CELLS,
  MAX_BOARD_EDGE,
  MAX_ID_LENGTH,
  MAX_SEAT_VALUE,
} from '../games/spec/schema.ts';
import type { RulesSpec } from '../games/spec/schema.ts';
import { END_REASONS, MATCH_RESULTS } from './domain.ts';
import { LobbyCodeSchema } from './lobbyCode.ts';
import { MAX_NAME_LENGTH } from './playerName.ts';

/**
 * The single definition of every WebSocket frame, imported by both the server
 * and the client. Schemas only — no behaviour, no I/O, no node builtins.
 *
 * ## Two layers, on purpose
 *
 * A frame names its game; it does not carry its game's rules. So validation is
 * split in two:
 *
 * 1. **Structural** (the zod schemas below). Bounded by the *spec system's*
 *    global caps — `MAX_SEAT_VALUE`, `MAX_BOARD_CELLS`, `MAX_BOARD_EDGE` — not
 *    by any one game. This layer runs at the socket edge, before the active
 *    spec is known, and its only job is to reject bytes that could not belong
 *    to *any* game.
 * 2. **Spec-aware** (`checkStatus`, `checkSnapshot`, `decodeStatus`, ...).
 *    Runs once the game id has been resolved to a `RulesSpec`, and checks the
 *    decoded value against *that* spec: seats it declares, its board geometry,
 *    its win lengths.
 *
 * This replaces the old `Exactly<WireStatus, Status>` compile-time proof, which
 * could only exist while every game was a compiled-in `const` with a literal
 * 4-cell win line. A platform whose games arrive as data cannot have that proof;
 * it gets `decodeStatus` instead, which is a *narrower* guarantee checked at
 * runtime rather than a wider one assumed at compile time.
 */
/**
 * Version 3 opened the frames up to people without a seat.
 *
 * Everything below used to assume that every recipient of a match frame was
 * playing in it: `yourSeat` was non-nullable and chat was addressed by match id
 * only. A host's queue is a room, though — the people in it watch the game and
 * talk through it — so a seat is now optional and chat is scoped to either a
 * match or a lobby.
 */
export const PROTOCOL_VERSION = 3;

/** How many server→client events a room retains for reconnect replay. */
export const EVENT_BUFFER_SIZE = 128;

/**
 * A seat number. Bounded by the largest seat *any* spec may declare, because a
 * frame is parsed before its spec is known. `checkSeat` narrows this to the
 * seats a particular game actually has.
 */
export const SeatSchema = z.number().int().min(1).max(MAX_SEAT_VALUE);
export type WireSeat = z.infer<typeof SeatSchema>;

/** Any id a spec may carry, built-in or authored. */
export const GameIdSchema = z.string().min(1).max(MAX_ID_LENGTH);
/** A move's single numeric parameter; for `drop` games, a column index. */
export const ColumnSchema = z.number().int().min(0).max(MAX_BOARD_EDGE - 1);
export const SeqSchema = z.number().int().min(1);
export const MatchIdSchema = z.uuid();
export const ClientMoveIdSchema = z.uuid();

export const BoardIndexSchema = z.number().int().min(0).max(MAX_BOARD_CELLS - 1);

/**
 * A win line is a variable-length array: its length is `terminal.win[].length`
 * in the active spec, which is data. `checkStatus` is what enforces that a line
 * is the right length *for this game* — see the module comment.
 */
export const StatusSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('in_play') }),
  z.object({
    kind: z.literal('win'),
    winner: SeatSchema,
    line: z.array(BoardIndexSchema).min(2).max(MAX_BOARD_EDGE).readonly(),
  }),
  z.object({ kind: z.literal('draw') }),
]);
export type WireStatus = z.infer<typeof StatusSchema>;

export const PlayerPublicSchema = z.object({
  userId: z.uuid(),
  displayName: z.string().min(1),
  rating: z.number().int(),
});
export type PlayerPublic = z.infer<typeof PlayerPublicSchema>;

/**
 * Seat number (as a decimal string key) to player. A record rather than a fixed
 * `{1, 2}` object, because seat count is `rules.seats.length`.
 */
const SeatsSchema = z.record(z.string().regex(/^[1-9]$/), PlayerPublicSchema);
export type Seats = z.infer<typeof SeatsSchema>;

export const ERROR_CODES = [
  'stale_seq',
  'not_your_turn',
  'illegal_move',
  'not_in_match',
  'rate_limited',
  'bad_frame',
  'protocol_version',
  'internal',
  /** The code was well-formed but matches no open lobby (expired, or a typo). */
  'lobby_not_found',
  /** You scanned your own QR code. Common on a stream; worth its own message. */
  'lobby_is_yours',
  /** You are already hosting, queued, or in a match. */
  'already_engaged',
  /**
   * The requested game id resolves to nothing playable: never published,
   * withdrawn since, or not seating the right number of players. Distinct from
   * `bad_frame` because the frame was perfectly well-formed.
   */
  'unknown_game',
  /** A host-only lobby action was sent by someone who does not host that lobby. */
  'not_lobby_host',
  /** You are not in the waiting list you tried to act on. */
  'not_waiting',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

// ---------------------------------------------------------------------------
// client -> server
// ---------------------------------------------------------------------------

export const HelloSchema = z.object({
  t: z.literal('hello'),
  protocolVersion: z.number().int(),
});

export const QueueSchema = z.object({
  t: z.literal('queue'),
  game: GameIdSchema,
});

export const LeaveQueueSchema = z.object({
  t: z.literal('leave_queue'),
});

export const ResumeSchema = z.object({
  t: z.literal('resume'),
  matchId: MatchIdSchema,
  /** Highest `seq` the client has already applied. 0 means "I have nothing". */
  sinceSeq: z.number().int().min(0),
});

export const MoveSchema = z.object({
  t: z.literal('move'),
  matchId: MatchIdSchema,
  /** Highest `seq` the client has applied. Must equal the room's `seq` exactly. */
  baseSeq: z.number().int().min(0),
  clientMoveId: ClientMoveIdSchema,
  move: z.object({ column: ColumnSchema }),
});

export const ResignSchema = z.object({
  t: z.literal('resign'),
  matchId: MatchIdSchema,
});

/**
 * Open a private lobby and get a shareable code back.
 *
 * This is the streamer's path: the host shows the code and its QR on screen, one
 * viewer joins, and they play. It bypasses the public queue entirely, so the
 * host always gets the opponent they intended.
 */
export const HostLobbySchema = z.object({
  t: z.literal('host_lobby'),
  game: GameIdSchema,
});

export const JoinLobbySchema = z.object({
  t: z.literal('join_lobby'),
  code: LobbyCodeSchema,
});

export const CancelLobbySchema = z.object({
  t: z.literal('cancel_lobby'),
});

/**
 * Reorder the waiting list, one step at a time.
 *
 * Deliberately a direction rather than a full `order: userId[]`: an array
 * assembled on the client races every join and leave that happened since it was
 * rendered, and applying a stale one would resurrect someone who left or drop
 * someone who just arrived. A direction is applied against the server's own
 * current list, so a stale click is a harmless no-op.
 */
export const LobbyMoveSchema = z.object({
  t: z.literal('lobby_move'),
  userId: z.uuid(),
  direction: z.enum(['up', 'down']),
});

/** Host picks who plays next. The rest of the queue stays put. */
export const LobbyStartSchema = z.object({
  t: z.literal('lobby_start'),
  userId: z.uuid(),
});

export const LobbyKickSchema = z.object({
  t: z.literal('lobby_kick'),
  userId: z.uuid(),
});

/** A waiting player withdraws from the queue they scanned into. */
export const LeaveLobbySchema = z.object({
  t: z.literal('leave_lobby'),
});

/** The longest a single chat line may be — about two lines at the panel's width. */
export const MAX_CHAT_LENGTH = 200;

/**
 * Where a chat line lives.
 *
 * A lobby outlasts the matches played from it: a gauntlet queue is one
 * continuous room, so scoping the conversation to a match would silence the
 * waiting players every time a game ended and reset it every time one started.
 * Public-queue matches, which have no lobby, keep the match scope.
 */
export const ChatScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('match'), matchId: MatchIdSchema }),
  z.object({ kind: z.literal('lobby'), code: LobbyCodeSchema }),
]);
export type ChatScope = z.infer<typeof ChatScopeSchema>;

export const SaySchema = z.object({
  t: z.literal('say'),
  scope: ChatScopeSchema,
  text: z.string().min(1).max(MAX_CHAT_LENGTH),
});

/**
 * Rename yourself.
 *
 * Accepts up to twice the display limit so an over-long attempt reaches the
 * server and is answered with a specific message, rather than being silently
 * truncated by the schema into a name nobody asked for.
 */
export const SetNameSchema = z.object({
  t: z.literal('set_name'),
  name: z.string().min(1).max(MAX_NAME_LENGTH * 2),
});

export const ClientFrameSchema = z.discriminatedUnion('t', [
  HelloSchema,
  QueueSchema,
  LeaveQueueSchema,
  ResumeSchema,
  MoveSchema,
  ResignSchema,
  HostLobbySchema,
  JoinLobbySchema,
  CancelLobbySchema,
  LobbyMoveSchema,
  LobbyStartSchema,
  LobbyKickSchema,
  LeaveLobbySchema,
  SaySchema,
  SetNameSchema,
]);
export type ClientFrame = z.infer<typeof ClientFrameSchema>;
export type ClientFrameType = ClientFrame['t'];

// ---------------------------------------------------------------------------
// server -> client
// ---------------------------------------------------------------------------

export const HelloOkSchema = z.object({
  t: z.literal('hello_ok'),
  protocolVersion: z.number().int(),
  userId: z.uuid(),
  displayName: z.string(),
  rating: z.number().int(),
  /** Set when the server already has a live match for this user, so the client can resume unprompted. */
  activeMatchId: MatchIdSchema.nullable(),
});

export const QueuedSchema = z.object({
  t: z.literal('queued'),
  game: GameIdSchema,
  position: z.number().int().min(1),
});

export const QueueLeftSchema = z.object({
  t: z.literal('queue_left'),
});

/**
 * Your lobby is open and waiting.
 *
 * `joinPath` is server-authored rather than assembled on the client, so the
 * QR code and the shared link can never disagree about the route.
 */
export const LobbyHostedSchema = z.object({
  t: z.literal('lobby_hosted'),
  code: LobbyCodeSchema,
  game: GameIdSchema,
  joinPath: z.string().min(1),
  expiresAt: z.iso.datetime(),
});

/**
 * One chat line.
 *
 * `chatSeq` is a **separate counter from the match `seq`**, and that separation
 * is load-bearing rather than tidy. A move is accepted only when its `baseSeq`
 * equals the room's current `seq` exactly, so if chat shared that counter, an
 * opponent typing between your reading the board and your clicking a column
 * would invalidate your move and force a resync. Chat would break gameplay.
 *
 * `from` is null for messages the room itself posts, and `from.seat` is null
 * for someone talking without a seat — a player waiting in the host's queue.
 */
export const ChatMessageSchema = z.object({
  t: z.literal('chat'),
  chatSeq: z.number().int().min(1),
  scope: ChatScopeSchema,
  from: z
    .object({
      userId: z.uuid(),
      displayName: z.string().min(1),
      seat: SeatSchema.nullable(),
    })
    .nullable(),
  kind: z.enum(['player', 'system']),
  text: z.string().min(1).max(MAX_CHAT_LENGTH),
  at: z.iso.datetime(),
});

/** Confirms a rename, so every client can update the name it is showing. */
export const NameChangedSchema = z.object({
  t: z.literal('name_changed'),
  userId: z.uuid(),
  displayName: z.string().min(1),
});

export const LobbyClosedSchema = z.object({
  t: z.literal('lobby_closed'),
  code: LobbyCodeSchema,
  /**
   * No 'matched': starting a match no longer closes the lobby. The host keeps
   * the code and the rest of the queue for the game after this one.
   */
  reason: z.enum(['cancelled', 'expired']),
});

/**
 * One person queued for the host's attention.
 *
 * Reuses `PlayerPublicSchema`'s field names rather than inventing a parallel
 * player shape, so a waiter and a seated player render through the same code.
 */
export const LobbyWaiterSchema = z.object({
  userId: z.uuid(),
  displayName: z.string().min(1),
  rating: z.number().int(),
  joinedAt: z.iso.datetime(),
});
export type LobbyWaiter = z.infer<typeof LobbyWaiterSchema>;

/**
 * The host's waiting list, in the order they will be offered a game.
 *
 * Sent whole on every change rather than as a diff: the list is short, and a
 * full replacement cannot drift out of step with the server the way a stream of
 * insertions and removals can.
 */
export const LobbyRosterSchema = z.object({
  t: z.literal('lobby_roster'),
  code: LobbyCodeSchema,
  waiting: z.array(LobbyWaiterSchema),
});

/**
 * You are in the queue. Re-sent whenever your place in it changes, so the
 * position on screen is never a stale promise.
 */
export const LobbyWaitingSchema = z.object({
  t: z.literal('lobby_waiting'),
  code: LobbyCodeSchema,
  game: GameIdSchema,
  hostName: z.string().min(1),
  position: z.number().int().min(1),
});

export const LobbyWaitingLeftSchema = z.object({
  t: z.literal('lobby_waiting_left'),
  reason: z.enum(['kicked', 'left', 'closed']),
});

/**
 * A match started or ended in the lobby you are waiting in.
 *
 * Waiters are attached to the running match as spectators, so they also receive
 * its `match_start`, `move_applied` and `match_end`. This frame is what ties
 * those to the lobby: it says which match belongs to this queue, and a null
 * `matchId` says the queue is between games again.
 */
export const LobbyMatchSchema = z.object({
  t: z.literal('lobby_match'),
  code: LobbyCodeSchema,
  game: GameIdSchema,
  matchId: MatchIdSchema.nullable(),
});

export const MatchStartSchema = z.object({
  t: z.literal('match_start'),
  seq: SeqSchema,
  matchId: MatchIdSchema,
  game: GameIdSchema,
  seats: SeatsSchema,
  /** Null for a spectator: someone in the host's queue watching the game ahead of theirs. */
  yourSeat: SeatSchema.nullable(),
  firstPlayerSeat: SeatSchema,
  startedAt: z.iso.datetime(),
});

export const MoveAppliedSchema = z.object({
  t: z.literal('move_applied'),
  seq: SeqSchema,
  matchId: MatchIdSchema,
  seat: SeatSchema,
  column: ColumnSchema,
  landedIndex: BoardIndexSchema,
  moveNumber: z.number().int().min(1).max(MAX_BOARD_CELLS),
  clientMoveId: ClientMoveIdSchema,
  status: StatusSchema,
  toMove: SeatSchema,
});

export const MatchEndSchema = z.object({
  t: z.literal('match_end'),
  seq: SeqSchema,
  matchId: MatchIdSchema,
  result: z.enum(MATCH_RESULTS),
  reason: z.enum(END_REASONS),
  line: z.array(BoardIndexSchema).nullable(),
  endedAt: z.iso.datetime(),
});

export const OpponentStatusSchema = z.object({
  t: z.literal('opponent_status'),
  seq: SeqSchema,
  matchId: MatchIdSchema,
  /** The seat whose connection changed. Each client reads it relative to itself. */
  seat: SeatSchema,
  state: z.enum(['connected', 'disconnected']),
  graceEndsAt: z.iso.datetime().nullable(),
});

export const SnapshotSchema = z.object({
  /**
   * `encodeBoard` output: one digit per cell, `'0'` plus the seat digits.
   *
   * Length and alphabet are only bounded here — the *exact* length is
   * `cols * rows` of the active spec, and the legal digits are the seats it
   * declares. `checkSnapshot` enforces both.
   */
  board: z
    .string()
    .regex(/^[0-9]+$/)
    .min(4)
    .max(MAX_BOARD_CELLS),
  toMove: SeatSchema,
  moveCount: z.number().int().min(0).max(MAX_BOARD_CELLS),
  status: StatusSchema,
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

export const MatchOutcomeSchema = z.object({
  result: z.enum(MATCH_RESULTS),
  reason: z.enum(END_REASONS),
  endedAt: z.iso.datetime(),
});
export type MatchOutcome = z.infer<typeof MatchOutcomeSchema>;

export const ResyncSchema = z.object({
  t: z.literal('resync'),
  seq: SeqSchema,
  matchId: MatchIdSchema,
  game: GameIdSchema,
  snapshot: SnapshotSchema,
  seats: SeatsSchema,
  /** Null for a spectator. See `match_start`. */
  yourSeat: SeatSchema.nullable(),
  firstPlayerSeat: SeatSchema,
  opponentConnected: z.boolean(),
  graceEndsAt: z.iso.datetime().nullable(),
  /**
   * Non-null once the match is over. A resign or abandon leaves no trace on the
   * board, so without this a resumed client could not tell a finished match from
   * a live one.
   */
  outcome: MatchOutcomeSchema.nullable(),
  /**
   * Recent chat, replayed on reconnect.
   *
   * Chat is outside the move sequence, so it cannot be recovered by the normal
   * `sinceSeq` replay. Carrying a backlog here is what stops a dropped
   * connection from wiping the conversation.
   */
  chat: z.array(ChatMessageSchema).optional(),
});

export const ErrorSchema = z.object({
  t: z.literal('error'),
  code: ErrorCodeSchema,
  message: z.string(),
  matchId: MatchIdSchema.optional(),
});

export const ServerFrameSchema = z.discriminatedUnion('t', [
  HelloOkSchema,
  QueuedSchema,
  QueueLeftSchema,
  LobbyHostedSchema,
  LobbyClosedSchema,
  LobbyRosterSchema,
  LobbyWaitingSchema,
  LobbyWaitingLeftSchema,
  LobbyMatchSchema,
  ChatMessageSchema,
  NameChangedSchema,
  MatchStartSchema,
  MoveAppliedSchema,
  MatchEndSchema,
  OpponentStatusSchema,
  ResyncSchema,
  ErrorSchema,
]);
export type ServerFrame = z.infer<typeof ServerFrameSchema>;
export type ServerFrameType = ServerFrame['t'];

export type HelloOkFrame = z.infer<typeof HelloOkSchema>;
export type QueuedFrame = z.infer<typeof QueuedSchema>;
export type LobbyHostedFrame = z.infer<typeof LobbyHostedSchema>;
export type LobbyClosedFrame = z.infer<typeof LobbyClosedSchema>;
export type LobbyRosterFrame = z.infer<typeof LobbyRosterSchema>;
export type LobbyWaitingFrame = z.infer<typeof LobbyWaitingSchema>;
export type LobbyWaitingLeftFrame = z.infer<typeof LobbyWaitingLeftSchema>;
export type LobbyMatchFrame = z.infer<typeof LobbyMatchSchema>;
export type ChatMessageFrame = z.infer<typeof ChatMessageSchema>;
export type NameChangedFrame = z.infer<typeof NameChangedSchema>;
export type MatchStartFrame = z.infer<typeof MatchStartSchema>;
export type MoveAppliedFrame = z.infer<typeof MoveAppliedSchema>;
export type MatchEndFrame = z.infer<typeof MatchEndSchema>;
export type OpponentStatusFrame = z.infer<typeof OpponentStatusSchema>;
export type ResyncFrame = z.infer<typeof ResyncSchema>;
export type ErrorFrame = z.infer<typeof ErrorSchema>;

/**
 * Every server frame except `hello_ok`, `queued`, `queue_left` and `error`
 * carries a `seq` and therefore participates in the ordered, replayable stream.
 */
export type SequencedServerFrame = Extract<ServerFrame, { seq: number }>;

export function isSequenced(frame: ServerFrame): frame is SequencedServerFrame {
  return 'seq' in frame;
}

// ---------------------------------------------------------------------------
// encoding
// ---------------------------------------------------------------------------

export type ParseResult<T> = { ok: true; frame: T } | { ok: false; reason: string };

function parseWith<T>(schema: z.ZodType<T>, raw: string): ParseResult<T> {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'frame is not valid JSON' };
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path.join('.') ?? '';
    return { ok: false, reason: `${path ? `${path}: ` : ''}${first?.message ?? 'invalid frame'}` };
  }
  return { ok: true, frame: parsed.data };
}

export function parseClientFrame(raw: string): ParseResult<ClientFrame> {
  return parseWith(ClientFrameSchema, raw);
}

export function parseServerFrame(raw: string): ParseResult<ServerFrame> {
  return parseWith(ServerFrameSchema, raw);
}

export function encodeFrame(frame: ClientFrame | ServerFrame): string {
  return JSON.stringify(frame);
}

// ---------------------------------------------------------------------------
// spec-aware validation
// ---------------------------------------------------------------------------

/**
 * The second validation layer. Everything below runs *after* the structural
 * schemas have accepted a frame and the game id has been resolved to a spec.
 *
 * These functions are what make the protocol genuinely spec-driven: the schemas
 * above know only the platform's outer limits, and these know the game.
 */
export type SpecCheck = { readonly ok: true } | { readonly ok: false; readonly reason: string };

const OK: SpecCheck = Object.freeze({ ok: true as const });

function fail(reason: string): SpecCheck {
  return { ok: false, reason };
}

/** True when `seat` is one of the seats this spec declares. */
export function isDeclaredSeat(rules: RulesSpec, seat: number): boolean {
  return (rules.seats as readonly number[]).includes(seat);
}

export function checkSeat(rules: RulesSpec, seat: number): SpecCheck {
  if (!isDeclaredSeat(rules, seat)) {
    return fail(`seat ${seat} is not one of [${rules.seats.join(', ')}]`);
  }
  return OK;
}

/** The win lengths this spec accepts, deduplicated. */
function winLengths(rules: RulesSpec): readonly number[] {
  return [...new Set(rules.terminal.win.map((condition) => condition.length))];
}

/**
 * Checks a decoded status against the active spec: a declared winner, a line
 * whose length is one this game can produce, and indices that are distinct and
 * inside this board.
 */
export function checkStatus(rules: RulesSpec, status: WireStatus): SpecCheck {
  if (status.kind !== 'win') return OK;

  const seatCheck = checkSeat(rules, status.winner);
  if (!seatCheck.ok) return seatCheck;

  const lengths = winLengths(rules);
  if (!lengths.includes(status.line.length)) {
    return fail(
      `win line has ${status.line.length} cells, but this game wins with [${lengths.join(', ')}]`,
    );
  }

  const cells = boardSize(rules);
  for (const index of status.line) {
    if (index >= cells) {
      return fail(`win line index ${index} is outside a ${cells}-cell board`);
    }
  }

  if (new Set(status.line).size !== status.line.length) {
    return fail('win line repeats a cell');
  }

  return OK;
}

/** Checks a decoded snapshot against the active spec's geometry and seats. */
export function checkSnapshot(rules: RulesSpec, snapshot: Snapshot): SpecCheck {
  const cells = boardSize(rules);
  if (snapshot.board.length !== cells) {
    return fail(`board has ${snapshot.board.length} cells, expected ${cells}`);
  }

  for (let i = 0; i < snapshot.board.length; i += 1) {
    const digit = Number(snapshot.board[i]);
    if (digit !== rules.cells.empty && !isDeclaredSeat(rules, digit)) {
      return fail(`board cell ${i} holds '${String(snapshot.board[i])}', which no seat owns`);
    }
  }

  const toMove = checkSeat(rules, snapshot.toMove);
  if (!toMove.ok) return toMove;

  if (snapshot.moveCount > cells) {
    return fail(`moveCount ${snapshot.moveCount} exceeds the ${cells}-cell board`);
  }

  return checkStatus(rules, snapshot.status);
}

/**
 * Validates a structurally-parsed status against `rules` and returns it as the
 * interpreter's own `Status<S>`.
 *
 * This is the runtime replacement for the deleted `Exactly<WireStatus, Status>`
 * proof. The single cast is the whole point: it is the one place where wire data
 * becomes engine data, it is unreachable unless every field has just been
 * checked against the active spec, and it is why that check has to be total.
 */
export function decodeStatus<S extends RulesSpec>(
  rules: S,
  status: WireStatus,
): { ok: true; status: Status<S> } | { ok: false; reason: string } {
  const check = checkStatus(rules, status);
  if (!check.ok) return { ok: false, reason: check.reason };
  return { ok: true, status: status as Status<S> };
}

/**
 * Applies the spec-aware layer to whichever parts of a frame carry game data.
 * Frames with no game-shaped payload pass trivially.
 */
export function checkServerFrame(rules: RulesSpec, frame: ServerFrame): SpecCheck {
  switch (frame.t) {
    case 'match_start': {
      // A null `yourSeat` is a spectator, not a bad frame.
      for (const seat of [frame.yourSeat, frame.firstPlayerSeat]) {
        if (seat === null) continue;
        const check = checkSeat(rules, seat);
        if (!check.ok) return check;
      }
      return checkSeats(rules, frame.seats);
    }

    case 'move_applied': {
      for (const seat of [frame.seat, frame.toMove]) {
        const check = checkSeat(rules, seat);
        if (!check.ok) return check;
      }
      if (frame.landedIndex >= boardSize(rules)) {
        return fail(`landedIndex ${frame.landedIndex} is outside this board`);
      }
      return checkStatus(rules, frame.status);
    }

    case 'match_end': {
      if (frame.line === null) return OK;
      const lengths = winLengths(rules);
      if (!lengths.includes(frame.line.length)) {
        return fail(
          `end line has ${frame.line.length} cells, but this game wins with [${lengths.join(', ')}]`,
        );
      }
      return OK;
    }

    case 'opponent_status':
      return checkSeat(rules, frame.seat);

    case 'resync': {
      for (const seat of [frame.yourSeat, frame.firstPlayerSeat]) {
        if (seat === null) continue;
        const check = checkSeat(rules, seat);
        if (!check.ok) return check;
      }
      const seats = checkSeats(rules, frame.seats);
      if (!seats.ok) return seats;
      return checkSnapshot(rules, frame.snapshot);
    }

    case 'chat':
      // A system line has no author and a waiter has no seat; anyone who does
      // claim a seat must claim one this game declares.
      if (frame.from === null || frame.from.seat === null) return OK;
      return checkSeat(rules, frame.from.seat);

    // No game-shaped payload: nothing for the spec layer to check.
    case 'hello_ok':
    case 'queued':
    case 'queue_left':
    case 'lobby_hosted':
    case 'lobby_closed':
    case 'lobby_roster':
    case 'lobby_waiting':
    case 'lobby_waiting_left':
    case 'lobby_match':
    case 'name_changed':
    case 'error':
      return OK;
  }
}

/** Every declared seat is present, and no undeclared seat is. */
function checkSeats(rules: RulesSpec, seats: Seats): SpecCheck {
  for (const seat of rules.seats) {
    if (seats[String(seat)] === undefined) return fail(`seats is missing seat ${seat}`);
  }
  for (const key of Object.keys(seats)) {
    if (!isDeclaredSeat(rules, Number(key))) {
      return fail(`seats carries seat ${key}, which this game does not declare`);
    }
  }
  return OK;
}
